import { useCallback, useMemo } from "react";
import { CoreFileItem } from "./FileCard";
import { triggerFileDownload } from "./fileHelper";

function getBaseFileData(file: CoreFileItem | File) {
	const isNativeFile = file instanceof File;
	return {
		name: file.name,
		type: file.type,
		nativeFile: isNativeFile ? file : file.nativeFile,
		url: isNativeFile ? undefined : file.url,
	};
}

function canOpenInNewTab(file: CoreFileItem | File): boolean {
	const { name, type } = getBaseFileData(file);

	const supportedMimeTypes = [
		"application/pdf",
		"image/jpeg",
		"image/png",
		"image/gif",
		"image/svg+xml",
		"image/webp",
		"video/mp4",
		"video/webm",
		"text/plain",
	];

	if (type && supportedMimeTypes.includes(type)) return true;

	// Fallback: Check extension
	const cleanedName = name.toLowerCase();
	const supportedExtensions = [".pdf", ".jpg", ".jpeg", ".png", ".gif", ".svg", ".webp", ".mp4", ".webm", ".txt"];

	return supportedExtensions.some((ext) => cleanedName.endsWith(ext));
}

export default function useOpenInNewTab(file: CoreFileItem | File) {
	const fileData = useMemo(() => getBaseFileData(file), [file]);

	const canOpenInTab = useMemo(
		() => canOpenInNewTab(file) && (!!fileData.url || !!fileData.nativeFile),
		[file, fileData],
	);

	const handleOpenInNewTab = useCallback(
		(fileOverride?: CoreFileItem | File) => {
			const targetFile = fileOverride || file;
			const fileToUse = getBaseFileData(targetFile);
			const canOpen = canOpenInNewTab(targetFile);

			if (fileToUse.url && canOpen) {
				window.open(fileToUse.url, "_blank", "noopener,noreferrer");
			} else if (canOpen && fileToUse.nativeFile) {
				const blobUrl = URL.createObjectURL(fileToUse.nativeFile as unknown as Blob);

				// Open the window with security features
				const newTab = window.open(blobUrl, "_blank", "noopener,noreferrer");
				if (newTab) {
					setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
				} else {
					// Fallback: if the popup was blocked, clean up immediately
					URL.revokeObjectURL(blobUrl);
				}
			} else if (fileToUse.nativeFile) {
				void triggerFileDownload(fileToUse.nativeFile);
			}
		},
		[file],
	);

	return {
		canOpenInTab,
		handleOpenInNewTab,
	};
}
